import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Trophy, TrendingUp, Target, Sparkles, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { sampleWinningTips } from '@/data/sampleData';

interface MatchFix {
  tip_id: string;
  title: string;
  sport: string;
  odds: number;
  confidence_level: number;
  persona_name: string;
  persona_avatar: string;
  win_rate: number;
  roi: number;
}

export function TopMatchFixes() {
  const [fixes, setFixes] = useState<MatchFix[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    fetchTopFixes();
  }, []);

  const fetchTopFixes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('top-match-fixes');

      if (error) throw error;

      if (data?.top_fixes && data.top_fixes.length > 0) {
        setFixes(data.top_fixes);
        setLastUpdated(data.updated_at ? new Date(data.updated_at) : new Date());
      } else {
        // Use sample data when no real data
        setFixes(sampleWinningTips);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching top match fixes:', error);
      // Use sample data on error
      setFixes(sampleWinningTips);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  const getSportIcon = (sport: string) => {
    const icons: Record<string, string> = {
      Football: '⚽',
      Basketball: '🏀',
      Tennis: '🎾',
      Baseball: '⚾',
      Hockey: '🏒'
    };
    return icons[sport] || '🎯';
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500 animate-pulse" />
            <CardTitle className="text-2xl">Loading Top Fixes...</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (fixes.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500" />
            <CardTitle className="text-2xl">Top Match Fixes</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            AI-verified winning tips from the last 30 days
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Loading winning tips...
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20 hover:shadow-2xl transition-all duration-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500 animate-bounce" />
            <CardTitle className="text-2xl bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
              Top 5 Match Fixes
            </CardTitle>
          </div>
          <Badge className="bg-green-500/20 text-green-700 border-green-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Verified Wins
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          AI-verified winning tips from the last 30 days
          {lastUpdated && (
            <span className="ml-2">• Updated {lastUpdated.toLocaleDateString()}</span>
          )}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {fixes.map((fix, index) => (
            <Card
              key={fix.tip_id}
              className="bg-card/50 hover:bg-card/80 border-border/50 hover:border-yellow-500/30 transition-all duration-300 hover:scale-[1.02] group"
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Rank Badge */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                        ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' : 
                          index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                          index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}
                      >
                        {index + 1}
                      </div>
                      {index === 0 && (
                        <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500 animate-pulse" />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{getSportIcon(fix.sport)}</span>
                          <h4 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {fix.title}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {fix.sport}
                          </Badge>
                          <Badge className="bg-green-500/20 text-green-700 border-green-500/30 text-xs">
                            Won
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-bold text-primary">{fix.odds.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">odds</div>
                      </div>
                    </div>

                    {/* Persona Info */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6 border border-border/50">
                          <AvatarImage src={fix.persona_avatar} />
                          <AvatarFallback className="text-xs bg-primary/10">
                            {fix.persona_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs font-medium text-foreground">{fix.persona_name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Target className="h-3 w-3" />
                              {fix.win_rate.toFixed(1)}% WR
                            </span>
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              {fix.roi > 0 ? '+' : ''}{fix.roi.toFixed(1)}% ROI
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge className="bg-purple-500/20 text-purple-700 border-purple-500/30 text-xs">
                        {fix.confidence_level}% Confidence
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            className="w-full bg-gradient-to-r from-yellow-500/10 to-orange-500/10 hover:from-yellow-500/20 hover:to-orange-500/20 border-yellow-500/30"
            asChild
          >
            <Link to="/tips" className="flex items-center justify-center gap-2">
              <Trophy className="h-4 w-4" />
              View All Winning Tips
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
